import { type EntitySource } from '../construction/source';

import { type EntityId } from './ids';
import { FillStyle, type Style } from './style';

/**
 * Scene entities.
 *
 * An entity is identity + presentation + a construction source. It deliberately does
 * **not** carry evaluated geometry: that lives in the engine's runtime scene and is
 * recomputed from the source, never persisted. Keeping the two apart is what makes a
 * saved file describe a construction rather than a snapshot of its appearance, and it is
 * why loading an old document produces correct geometry under a newer evaluator.
 */

/** What an entity *is*, geometrically. Derived from its source; never stored separately. */
export const EntityKind = {
  Solid: 'solid',
  AngleMark: 'angle-mark',
  Point: 'point',
  Segment: 'segment',
  Ray: 'ray',
  Line: 'line',
  Vector: 'vector',
  Circle: 'circle',
  Arc: 'arc',
  Ellipse: 'ellipse',
  Polygon: 'polygon',
  /** A plane of the figure, drawn as its section with the solid it cuts. */
  Plane: 'plane',
  Polyline: 'polyline',
  Text: 'text',
  Measurement: 'measurement',
  Image: 'image',
  FunctionGraph: 'function-graph',
  Axes: 'axes',
  VariationTable: 'variation-table',
  /** The notable points of a graph, dotted and labelled, as one derived object. */
  GraphMarks: 'graph-marks',
} as const;
export type EntityKind = (typeof EntityKind)[keyof typeof EntityKind];

export interface Entity {
  readonly id: EntityId;
  readonly source: EntitySource;
  readonly style: Style;

  /** Draw order within the document. Higher paints later. */
  readonly zIndex: number;

  readonly visible: boolean;

  /** Locked entities cannot be selected or dragged, but still update with their parents. */
  readonly locked: boolean;

  /**
   * Marks helper geometry. Distinct from `visible` because "hide construction" is a
   * view mode that must be reversible without losing the user's own visibility choices.
   */
  readonly isConstruction: boolean;

  /** Display name, e.g. `A`, `BC`, `ω₁`. Absent when the entity is unlabelled. */
  readonly label?: string;
}

/**
 * The geometric kind an operation produces.
 *
 * Exhaustive by construction: adding an `EntitySource` variant without a case here is a
 * compile error, so no entity can reach the renderer with an unknown kind.
 */
/**
 * How the next plane in a figure is toned, given how many are already in it.
 *
 * ## Why the second one is not the first one again
 *
 * Because two planes are usually drawn to be seen *crossing*, and where they cross a reader has
 * to be able to say which is which. Two identical washes make that impossible: the overlap is the
 * same grey as both, and the figure loses the one thing it was drawn to show. `(SAC)` and `(SBD)`
 * in a pyramid have the same problem as two blackboard planes.
 *
 * ## Why hatching rather than a second colour
 *
 * Three reasons, and the third is the one that decides it:
 *
 * - It is the drafting convention for a section, so it reads as one to anybody who has seen a
 *   technical drawing.
 * - It survives a **photocopier**. `STYLE_PRESETS.plane` records that the pale wash sits near the
 *   limit of what a copier keeps — "somewhere below about ten per cent a tint stops being a tint
 *   and becomes paper" — so a figure told apart by two tints is told apart until it is copied.
 * - It survives **black and white**, which is what a worksheet is printed in.
 *
 * The hatch is drawn in a mid grey rather than the wash colour, because hatch lines take their
 * colour from the fill and `#eef1f6` lines on white are not there at all.
 *
 * ## A starting point, not a rule
 *
 * Every one of these is a *default* the properties panel overrides like any other style. What this
 * decides is what a teacher gets before they have said anything, and the answer to that should not
 * be "a figure you cannot read".
 */
export function toneForPlane(existing: number): Partial<Style> {
  const chosen = PLANE_TONES[Math.abs(Math.trunc(existing)) % PLANE_TONES.length];
  // The index is taken modulo the list's own length, so this cannot happen — and if the list is
  // ever emptied it is the plain wash that a figure should fall back to, not no fill at all.
  return chosen ?? WASH;
}

/**
 * The two ways a plane arrives, alternating.
 *
 * The first is exactly what every plane has looked like until now, so a figure with one plane in
 * it — which is most figures — is unchanged, and so is every drawing already saved.
 */
const WASH: Partial<Style> = { fill: '#eef1f6', fillStyle: FillStyle.Solid };

const PLANE_TONES: readonly Partial<Style>[] = [
  WASH,
  { fill: '#94a3b8', fillStyle: FillStyle.Hatch },
];

/**
 * Whether this source draws a **plane**, and so arrives toned rather than as a bare outline.
 *
 * Read off {@link kindOf} rather than kept as a second list, because a second list has now been
 * got wrong twice in the same way: `plane-turn` shipped untoned and so did `plane-free`, each
 * because a set of op names was extended in the tool and not in the parser, or the other way
 * round. Both times the symptom was the very defect `STYLE_PRESETS.plane` exists to prevent — a
 * plane drawn as three more line segments, indistinguishable from three more edges of the figure —
 * and both times only an exported drawing showed it.
 *
 * `kindOf` is already exhaustive over every op, and already the one place that decides what sort
 * of thing each source is. Asking it means there is nothing separate left to forget.
 */
export function isPlaneSource(source: EntitySource): boolean {
  return kindOf(source) === EntityKind.Plane;
}

export function kindOf(source: EntitySource): EntityKind {
  switch (source.op) {
    case 'solid':
    case 'solid-figure':
    case 'polyhedron':
    case 'solid-cut':
      return EntityKind.Solid;

    /*
     * Its own kind, not `Polygon`, even though a section is drawn as one. A user asked for a
     * *plane*; the object list should say so, and a wash the size of a face should not compete
     * with a small polygon for a click.
     */
    // `plane-free` is here too: a blackboard plane is a plane, whatever it hangs from.
    case 'plane':
    case 'plane-parallel':
    case 'plane-turn':
    case 'plane-free':
      return EntityKind.Plane;

    /*
     * A viewpoint has no geometry, and `EntityKind` has no word for "not a shape". `Point` is
     * the least wrong of the twenty: it is what the frame's origin *is*, it is invisible so
     * nothing is ever drawn or picked for it, and every kind-based filter that offers a control
     * for a point offers one that does nothing rather than one that does harm.
     */
    case 'space':
      return EntityKind.Point;

    // The giao tuyến really is a segment, and every control that already reaches a segment
    // should reach it: dashing behind the body, measuring its length, naming it.
    case 'plane-meet':
      return EntityKind.Segment;

    // A foot is a point, and everything that reaches a point should reach it: a segment drawn
    // to it, a distance measured from it, a right-angle mark placed at it.
    case 'plane-foot':
      return EntityKind.Point;

    case 'angle-mark':
    case 'dihedral-mark':
      return EntityKind.AngleMark;

    case 'free-point':
    case 'locus-point':
    case 'plane-point':
    case 'solid-vertex':
    case 'solid-rim-point':
    case 'solid-foot':
    case 'midpoint':
    case 'ratio-point':
    case 'segment-midpoint':
    case 'intersection':
    case 'projected-point':
    case 'reflected-point':
    case 'rotated-point':
    case 'translated-point':
    case 'scaled-point':
    case 'circumcenter':
    case 'incenter':
    case 'centroid':
    case 'orthocenter':
    case 'circle-center':
      return EntityKind.Point;

    case 'segment':
      return EntityKind.Segment;

    case 'ray':
    case 'angle-bisector':
      return EntityKind.Ray;

    case 'line':
    case 'parallel-line':
    case 'perpendicular-line':
    case 'tangent-line':
    case 'graph-tangent':
    case 'perpendicular-bisector':
      return EntityKind.Line;

    case 'vector':
      return EntityKind.Vector;

    case 'circle-through':
    case 'circle-radius':
    case 'circle-3-points':
      return EntityKind.Circle;

    case 'arc-3-points':
    case 'arc-center':
      return EntityKind.Arc;

    case 'ellipse':
    case 'ellipse-foci':
    case 'free-ellipse':
      return EntityKind.Ellipse;

    case 'polygon':
    case 'polygon-boolean':
    case 'regular-polygon':
      return EntityKind.Polygon;

    case 'free-polyline':
      return EntityKind.Polyline;

    case 'text':
      return EntityKind.Text;

    case 'measurement':
      return EntityKind.Measurement;

    case 'function-graph':
      return 'function-graph';

    case 'axes':
      return 'axes';

    case 'variation-table':
      return EntityKind.VariationTable;

    case 'graph-marks':
      return EntityKind.GraphMarks;

    case 'image':
      return EntityKind.Image;
  }
}

/**
 * True for kinds with no finite extent.
 *
 * The renderer must clip these to the viewport rather than draw them from endpoint to
 * endpoint, and the spatial index cannot store their true bounds — both need to ask.
 */
export function isUnbounded(kind: EntityKind): boolean {
  return kind === EntityKind.Line || kind === EntityKind.Ray;
}

export interface CreateEntityOptions {
  readonly zIndex?: number;
  readonly visible?: boolean;
  readonly locked?: boolean;
  readonly isConstruction?: boolean;
  readonly label?: string;
}

/**
 * Builds an entity with sensible defaults.
 *
 * `label` is omitted rather than set to `undefined` when absent: with
 * `exactOptionalPropertyTypes`, "property absent" and "property present and undefined"
 * are different types, and the serializer relies on that difference to round-trip
 * losslessly.
 */
export function createEntity(
  id: EntityId,
  source: EntitySource,
  style: Style,
  options: CreateEntityOptions = {}
): Entity {
  const base = {
    id,
    source,
    style,
    zIndex: options.zIndex ?? 0,
    visible: options.visible ?? true,
    locked: options.locked ?? false,
    isConstruction: options.isConstruction ?? false,
  };
  return options.label === undefined ? base : { ...base, label: options.label };
}
